import { type MouseEvent, memo, useCallback, useState } from "react";

import { parseGid } from "@shopify/hydrogen-react";
import {
  type ProductOption,
  type Maybe,
  type FilterValue,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Badge, type BadgeType } from "@components/Badge";
import { Heading } from "@components/Heading";
import { Image } from "@components/Image";
import { Link } from "@components/Link";
import { Price } from "@components/Price";
import {
  type CompareAtPriceDisplay,
  PriceContext,
} from "@components/Price/constants";
import { Text } from "@components/Text";
import { focusRingVisible } from "@react/utilities";
import { type CartMediaImage, type OPProductVariant } from "@tablescape/state";
import { type TTailwindString, tw } from "classnames";

import { ProductCardOptions } from "./ProductCardOptions.data";
import { ProductCardQuickAdd } from "./ProductCardQuickAdd.data";

type Props = {
  activeVariant: OPProductVariant;
  activeVariantUrl: string;
  appliedColorFilters?: FilterValue[];
  badgeText: Maybe<string>;
  badgeType: Maybe<BadgeType>;
  bundleValueText: string;
  colorOption?: Omit<ProductOption, "values">;
  compareAtPriceDisplay?: Maybe<CompareAtPriceDisplay>;
  description: Maybe<string>;
  enableQuickAdd?: boolean;
  labels?: string[];
  nextLabel: string;
  pageType?: string;
  prevLabel: string;
  productId: string;
  quickAddButtonText?: string;
  setSize?: Maybe<string>;
  showQuickAdd?: boolean;
  title: string;
  variantMatchCollectionName?: string;
};

const styles = {
  base: tw("block", "group", "group/card" as TTailwindString, "relative"),
  cardWrapper: tw("relative"),
  cardLink: tw(
    "block",
    "no-underline",
    "hover:no-underline" as TTailwindString
  ),
  imageHotZone: tw(
    "group",
    "relative",
    "absolute",
    "inset-0",
    "w-full",
    "h-full"
  ),
  imageAndOverlayGroup: tw(
    "group",
    "group/hotzone" as TTailwindString,
    "relative",
    "aspect-square",
    "w-full"
  ),
  image: tw("bg-background-secondary", "relative", "h-full", "w-full"),
  imageArea: tw(
    "group",
    "group/image" as TTailwindString,
    "h-full",
    "w-full",
    "relative"
  ),
  imageFadeIn: tw("animate-fade-in"),
  hoverImage: tw(
    "absolute",
    "left-0",
    "top-0",
    "z-0",
    "hidden",
    "opacity-0",
    "transition-opacity",
    "md:block",
    "md:group-hover/hotzone:opacity-100" as TTailwindString,
    "md:group-data-[quick-add-hovered=true]/card:opacity-100" as TTailwindString
  ),
  badge: tw("absolute", "left-2", "top-2", "md:left-4", "md:top-4"),
  content: tw(
    "mt-2",
    "flex",
    "flex-1",
    "flex-col",
    "justify-between",
    "md:mt-4"
  ),
  quickAddOverlay: tw(
    "pointer-events-none",
    "absolute",
    "top-0",
    "left-0",
    "right-0",
    "w-full",
    "aspect-square",
    "z-[1]" as TTailwindString
  ),
  titleContainer: tw("flex", "gap-x-1", "mb-1", "items-baseline", "flex-wrap"),
  title: tw("text-primary"),
  setSize: tw("text-primary"),
  description: tw("mb-1.5", "hidden", "text-primary-muted", "md:block"),
  price: tw("mb-2"),
  labels: (colorOptionValueLength: number) =>
    tw("flex", "overflow-x-auto", "flex", "flex-wrap", "gap-2", "p-0", {
      [tw("mt-1", "md:mt-2")]: colorOptionValueLength === 0,
      [tw("mt-4")]: colorOptionValueLength > 0,
    }),
  label: tw("hidden", "first:flex", "md:flex"),
};

const ProductCardViewBase = ({
  activeVariant,
  activeVariantUrl,
  appliedColorFilters,
  badgeText,
  badgeType,
  bundleValueText,
  colorOption,
  compareAtPriceDisplay,
  description,
  enableQuickAdd,
  labels,
  nextLabel,
  pageType,
  prevLabel,
  productId,
  quickAddButtonText,
  setSize,
  showQuickAdd = true,
  title,
  variantMatchCollectionName,
}: Props) => {
  const { id: activeVariantId } = activeVariant;
  const [isQuickAddHovered, setIsQuickAddHovered] = useState(false);

  const handleOptionsContainerClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    },
    []
  );

  return (
    <li
      className={styles.base}
      data-product-id={parseGid(productId).id}
      data-product-variant-id={parseGid(activeVariantId).id}
      {...(isQuickAddHovered && {
        "data-quick-add-hovered": "true",
      })}
    >
      <div className={styles.cardWrapper}>
        <Link
          className={tw(styles.cardLink, focusRingVisible)}
          data-mtr-as="clickProduct"
          data-mtr-canonical-href={activeVariantUrl}
          data-mtr-product-variant-name={activeVariant.title}
          data-mtr-track="click"
          to={activeVariantUrl}
        >
          <div className={styles.imageAndOverlayGroup}>
            <div className={styles.imageHotZone}>
              <div className={styles.image}>
                <div className={styles.imageArea}>
                  {activeVariant.media[0] && (
                    <div className={styles.imageFadeIn}>
                      <Image
                        aspectRatio="1/1"
                        data={(activeVariant.media[0] as CartMediaImage).image}
                        height={476}
                        width={476}
                      />
                    </div>
                  )}
                  {activeVariant.hoverImage && (
                    <div className={styles.hoverImage}>
                      <Image
                        aspectRatio="1/1"
                        data={activeVariant.hoverImage.image}
                        height={476}
                        width={476}
                      />
                    </div>
                  )}
                  {badgeText && (
                    <div
                      className={styles.badge}
                      id={`product-card-${parseGid(activeVariantId).id}-badge`}
                    >
                      <Badge
                        size={{ sm: "sm", md: "md" }}
                        {...(badgeType && { type: badgeType })}
                      >
                        {badgeText}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.titleContainer}>
              <Heading as="h6" className={styles.title}>
                {title}
              </Heading>
              {setSize && <Text className={styles.setSize}>({setSize})</Text>}
            </div>
            {description && (
              <Text className={styles.description}>{description}</Text>
            )}
            <div className={styles.price}>
              <Price
                bundleValueText={bundleValueText}
                compareAtPrice={activeVariant.compareAtPrice}
                compareAtPriceDisplay={compareAtPriceDisplay}
                context={PriceContext.PRODUCT_CARD}
                price={activeVariant.price}
                showCurrencySuffix={false}
                size="md"
              />
            </div>
            <div onClick={handleOptionsContainerClick} role="presentation">
              <ProductCardOptions
                appliedColorFilters={appliedColorFilters}
                nextLabel={nextLabel}
                prevLabel={prevLabel}
                variantMatchCollectionName={variantMatchCollectionName}
              />
            </div>
            {labels?.length ? (
              <ol
                className={styles.labels(colorOption?.optionValues.length ?? 0)}
              >
                {labels.map((label) => (
                  <li className={styles.label} key={label}>
                    <Badge type="quinary">{label}</Badge>
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        </Link>
        {enableQuickAdd && showQuickAdd && (
          <div className={styles.quickAddOverlay} role="presentation">
            <ProductCardQuickAdd
              onMouseEnter={() => setIsQuickAddHovered(true)}
              onMouseLeave={() => setIsQuickAddHovered(false)}
              pageType={pageType}
              quickAddButtonText={quickAddButtonText}
            />
          </div>
        )}
      </div>
    </li>
  );
};

export const ProductCardView = memo(ProductCardViewBase);
